"use client";

import { AnimatePresence } from "motion/react";
import { memo, type Ref, useCallback, useMemo } from "react";
import type { MockMessage, MockMessageButton } from "@/dummy";
import type { InternalLink } from "@/lib/internal-link";
import { DateSeparator } from "./message/date-separator";
import type { LightboxItem } from "./message/media-lightbox";
import { MessageBubble } from "./message/message-bubble";
import { SystemNotice } from "./message/system-notice";
import { UnreadDivider } from "./message/unread-divider";
import type { MessageRow as Row } from "./message-rows";
import { type Entrance, useRowEntrances } from "./row-entrance";

interface RowIdentity {
  organizationLogoUrl?: string | null;
  organizationName?: string;
  aiAssistantAvatarUrl?: string | null;
  aiAssistantName?: string | null;
  onOpenLightbox: (items: LightboxItem[], index: number) => void;
  onInternalLink?: (link: InternalLink) => void;
}

interface MessageRowProps extends RowIdentity {
  message: MockMessage;
  /** A read receipt arrived over SSE for this id: force `deliveryStatus="READ"`
   *  regardless of what the history fetch said (avoids a refetch just to flip
   *  a glyph). */
  read: boolean;
  /** Demo only: the mock operator's avatar tracks the live operator-avatar
   *  config (first entry) rather than a baked value, so clearing avatars in the
   *  playground clears them here too. */
  trackOperatorAvatar: boolean;
  operatorAvatarSrc?: string;
  buttonsActive: boolean;
  enter: Entrance;
  onQuickReply: (messageId: string, button: MockMessageButton) => void;
  onRetry?: (messageId: string) => void;
}

/**
 * One message row. Everything it receives is a primitive or a stable identity,
 * so `memo` skips it unless THIS message (or its read / buttons state) changed -
 * the thread no longer re-renders per keystroke, typing beat or incoming message.
 */
const MessageRow = memo(function MessageRow({
  message,
  read,
  trackOperatorAvatar,
  operatorAvatarSrc,
  buttonsActive,
  enter,
  onQuickReply,
  onRetry,
  ...identity
}: MessageRowProps) {
  const shown = useMemo(() => {
    let m = read ? ({ ...message, deliveryStatus: "READ" } as MockMessage) : message;
    if (trackOperatorAvatar && m.sender.kind === "operator") {
      m = { ...m, sender: { ...m.sender, avatar: operatorAvatarSrc } };
    }
    return m;
  }, [message, read, trackOperatorAvatar, operatorAvatarSrc]);
  const id = message.id;
  const handleButton = useCallback(
    (btn: MockMessageButton) => onQuickReply(id, btn),
    [onQuickReply, id],
  );
  const handleRetry = useCallback(() => onRetry?.(id), [onRetry, id]);
  return (
    <MessageBubble
      message={shown}
      buttonsActive={buttonsActive}
      enter={enter}
      onButtonClick={handleButton}
      onRetry={message.deliveryStatus === "FAILED" ? handleRetry : undefined}
      {...identity}
    />
  );
});

export interface MessageListProps extends RowIdentity {
  rows: Row[];
  readMessageIds: ReadonlySet<string>;
  isDummy: boolean;
  operatorAvatarSrc?: string;
  activeButtonMessageId: string | null;
  unreadLabel: string;
  unreadDividerRef: Ref<HTMLDivElement>;
  /** Stable (see `useStableCallback`) - a fresh closure would re-render every row. */
  onQuickReply: (messageId: string, button: MockMessageButton) => void;
  onRetry?: (messageId: string) => void;
}

/**
 * The conversation rows (messages, date separators, the unread divider).
 * Memoised: it re-renders only when the row list or one of its inputs changes,
 * not when the composer draft or the typing indicator does.
 */
export const MessageList = memo(function MessageList({
  rows,
  readMessageIds,
  isDummy,
  operatorAvatarSrc,
  activeButtonMessageId,
  unreadLabel,
  unreadDividerRef,
  onQuickReply,
  onRetry,
  ...identity
}: MessageListProps) {
  const entrances = useRowEntrances(
    rows.flatMap((r) => (r.kind === "message" && !r.message.system ? [r.key] : [])),
  );
  return (
    // `initial={false}`: rows enter via the CSS `wx-gentle-in` (see `row-entrance.ts`), decided per
    // row on first sight - the opening list staggers its last rows, history never flies in.
    // `presenceAffectsLayout={false}`: by default every re-render hands each row a
    // fresh presence context, which re-renders (and re-measures) all of them for
    // one new message. Rows are in normal flow, so a sibling's enter/exit needs
    // no help from its neighbours to land in the right place.
    <AnimatePresence initial={false} presenceAffectsLayout={false}>
      {rows.map((row) => {
        if (row.kind === "date") return <DateSeparator key={row.key} label={row.label} />;
        if (row.kind === "unread") {
          return <UnreadDivider key={row.key} ref={unreadDividerRef} label={unreadLabel} />;
        }
        const msg = row.message;
        if (msg.sender.kind === "system" && msg.system) {
          return <SystemNotice key={row.key} system={msg.system} messageId={msg.id} />;
        }
        return (
          <MessageRow
            key={row.key}
            message={msg}
            read={readMessageIds.has(msg.id)}
            trackOperatorAvatar={isDummy}
            operatorAvatarSrc={operatorAvatarSrc}
            buttonsActive={msg.id === activeButtonMessageId}
            enter={entrances.get(row.key) ?? null}
            onQuickReply={onQuickReply}
            onRetry={onRetry}
            {...identity}
          />
        );
      })}
    </AnimatePresence>
  );
});
